import type { BackgroundOption } from './Background'
import type { WithDisplayColor } from './Colors'
import { type DatabaseMetadata } from '~/types/DatabaseMetadata'
import { type WithAuthoringStatus } from '~/types/AuthoringStatus'
import { type WithDisplayIndex } from '~/types/DisplayIndex'
import { type NumberOrNull } from '~/types/NumberOrNull'
import type { UploadedFile } from '~/types/UploadedFile'
import { ExternalContentType } from '~/types/ExternalContentType'
import type { CheckStatus } from '~/types/TestCheck'

export enum TestFormat {
  Mcq = 'mcq',
  Likert = 'likert',
  Written = 'written',
  Simulation = 'simulation',
  SimulationV2 = 'simulation-v2',
  Submission = 'file-submission',
}

export enum SubmissionType {
  Document = 'document',
  Audio = 'audio',
  Video = 'video',
  // Screen = 'screen' // Future
}

// A grading statement on a points-based category is either scoring (carries
// points, awarded on its own merits) or qualifying (no points; judged met or
// not met, and the scoring statements it governs in its own category can be
// awarded only if it was met). studio-api decision record 0023.
export enum StatementKind {
  Points = 'points',
  Qualifying = 'qualifying',
}

export interface TestGradingCategoryStatement {
  id: string
  statement: string
  // Null on a qualifying statement, which carries no points.
  points: number | null
  // Absent on statements saved before qualifiers existed: read it through
  // `isQualifyingStatement`, never compare it directly.
  kind?: StatementKind
  // Qualifying only: ids of the sibling scoring statements it governs. Null or
  // empty governs every scoring sibling.
  governs?: string[] | null
  displayIndex: number
  testGradingCategoryId: string
  testId: string
}

export enum RubricType {
  LevelBased = 'level-based',
  PointsBased = 'points-based',
}

export enum TestFormatV2 {
  Question = 'question',
  Simulation = 'simulation',
  SimulationV2 = 'simulation-v2',
  Submission = 'submission',
}

export interface TestGradingCategoryLevel extends WithAuthoringStatus {
  id: string
  level: NumberOrNull
  requiredPercentage: NumberOrNull // this becomes user input
  requiredPoints: NumberOrNull // this is FE-side computed: = pointsAvailableFromQuestions * (requiredPercentage / 100)
  title: string
  description: string
  gradingStatements: string[]
}

export interface TestGradingCategory
  extends WithAuthoringStatus,
    WithDisplayColor,
    WithDisplayIndex {
  id: string
  title: string
  levels: TestGradingCategoryLevel[]
  weighting: NumberOrNull
  maxAvailablePoints: number
  description: string
  instructions: string
  gradeEntireSubmission: boolean
  contentToGradeInstruction: string
  outputFeedback: boolean
  feedbackInstruction: string
  contextFile: UploadedFile | null
  contextFileContext: string | null
  originalContextFiles: UploadedFile[]
  markdownContextFiles: UploadedFile[]
  gradingGuidelines: string
  feedbackExample: string
  modelAnswer: string
  parentId: string | null
  displayLabel: string | null
  statements: TestGradingCategoryStatement[]
  children: TestGradingCategory[]
}

export enum GradingType {
  Skills = 'skills',
  Competency = 'competency',
  Preference = 'preference',
  Knowledge = 'knowledge',
}

export enum McqChoiceType {
  Text = 'text',
  Image = 'image',
}

export interface Choice extends WithDisplayIndex {
  id: string
  text: string

  // MCQ
  correct: boolean
  imageUrl: string
  points: NumberOrNull

  // Likert
  value: NumberOrNull
}

export interface WrittenQuestionGradingCategoryLevel {
  gradingStatements: string[]
  description: string
  requiredPoints: NumberOrNull
  level: NumberOrNull
  id: string
}

export interface WrittenQuestionGradingCategory extends WithDisplayIndex {
  testGradingCategoryId: string | null // GradingCategory.id
  levels: WrittenQuestionGradingCategoryLevel[]
}

// !!
export interface SimulationQuestionGradingCategoryLevel {
  description: string
  requiredPoints: NumberOrNull
}

// !!
export interface SimulationGradingCategory {
  testGradingCategoryId: string // GradingCategory.id
  levels: SimulationQuestionGradingCategoryLevel[]
}

export enum GradingContextType {
  ModelAnswer = 'model-answer',
}

export interface GradingContext {
  type: GradingContextType | null
  text: string
}

/**
 * **Confidence rating**
 * [incorrect, correct]
 */
export type ConfidenceRatingEntry = [NumberOrNull, NumberOrNull]

export interface ConfidenceRating
  extends Record<number, ConfidenceRatingEntry> {
  5: ConfidenceRatingEntry
  4: ConfidenceRatingEntry
  3: ConfidenceRatingEntry
  2: ConfidenceRatingEntry
  1: ConfidenceRatingEntry
}

// export interface QuestionGrading {
//   requiresGradingNote: boolean
//   gradingNoteInstructions: string
//   rubric: WrittenQuestionGradingCategory[] | SimulationGradingCategory[]
//   gradingContext: GradingContext
//   testGradingCategoryId: string | null
// }

export interface Question extends WithAuthoringStatus, WithDisplayIndex {
  id: string
  required: boolean
  heading: string | null
  text: string | null
  imageUrl: string | null
  externalContentUrl: string | null
  externalContentType: ExternalContentType | null

  // MCQ
  choiceType: McqChoiceType | null

  // Written & MCQ
  confidenceRating: ConfidenceRating

  // MCQ & Likert
  choices: Choice[]

  // Simulation
  simulationId: string | null

  requiresGradingNote: boolean
  gradingNoteInstructions: string
  rubric: WrittenQuestionGradingCategory[]
  gradingContext: GradingContext
  testGradingCategoryId: string | null

  // File Submission
  fileSubmissionTestId: string | null
  hasFileSubmissionTest: boolean
}

export enum CategoryScoreAllocation {
  ActualUserScore = 'actual-user-score',
  CategoryLevelScore = 'category-level-score',
}

export enum CategoryScoreWeighting {
  Manual = 'manual',
  Points = 'points',
}

// export interface TestGrading {
//   categoryScoreAllocation: CategoryScoreAllocation | null
//   categoryScoreWeighting: CategoryScoreWeighting | null
//   rubric: TestGradingCategory[]
//   type: GradingType | null
//   maxAvailablePoints: number
// }

export enum TestType {
  TechnicalSkills = 'technical-skills',
  SoftSkills = 'soft-skills',
  HardSkills = 'hard-skills',
  Personality = 'personality',
  Custom = 'custom',
}

export interface GradingConfidenceRatings {
  veryConfident: number
  confident: number
}

export interface FeedbackSettings {
  feedbackSummaryContext?: string | null
  showCategoryFeedback?: boolean
  showFeedbackSummary?: boolean
  showFeedbackOnTestCompletion?: boolean
  generateReport?: boolean
  showFeedbackChat?: boolean
  showFeedbackHeader?: boolean
}

export const defaultFeedbackSettings: Required<FeedbackSettings> = {
  feedbackSummaryContext: null,
  showCategoryFeedback: false,
  showFeedbackSummary: false,
  showFeedbackOnTestCompletion: false,
  generateReport: false,
  showFeedbackChat: false,
  showFeedbackHeader: true,
}

export interface Test extends DatabaseMetadata, WithAuthoringStatus {
  id: string
  createdBy: string
  updatedBy: string | null
  format: TestFormat
  // testType: TestType | null // ! DEPRECATED
  submissionType: SubmissionType | null
  title: string
  isPublished: boolean
  heading: string
  // requiresBlurReporting: boolean // ! DEPRECATED
  useTimeLimit: boolean
  durationSeconds: NumberOrNull
  useSingleTakeMode: boolean
  useLimitedQuestionPreview: boolean
  useGradingVariabilityDetection?: boolean
  questionPreviewSeconds: NumberOrNull
  introduction: string
  objective: string
  questions: Question[]
  uploadedFile: UploadedFile | null
  shouldRandomiseQuestionOrder: boolean
  // forceFullScreen: boolean // ! DEPRECATED

  // MCQ ------------------------------
  hasConfidenceRating: boolean
  shouldRandomiseResponseOptions: boolean

  // Defaults ------------------------------
  choicesDefault: Omit<Choice, 'id'>[] | null
  confidenceRatingsDefault: ConfidenceRating | null

  // And of course
  primaryWorkspaceId: string
  workspaces: string[]
  isGlobal: boolean

  categoryScoreAllocation: CategoryScoreAllocation | null
  categoryScoreWeighting: CategoryScoreWeighting | null
  rubricType: RubricType
  rubric: TestGradingCategory[]
  gradingType: GradingType | null
  maxAvailablePoints: number

  originalContextFiles: UploadedFile[]
  markdownContextFiles: UploadedFile[]
  contextFileContext: string
  gradingConfidenceRatings: GradingConfidenceRatings
  submissionContext: string
  lockedAt: string | null
  lockedBy: string | null

  // Where the test stands with its quality checks. Maintained solely by the
  // api's checks service, and never sent in a PATCH payload (the api deletes it
  // from any body it receives). `NOT NULL DEFAULT 'not-started'` in the DB, so
  // required rather than optional. See `docs/domains/test-checks.md`.
  checkStatus: CheckStatus

  autoGrade: boolean

  background: BackgroundOption | null

  simulationId: string | null

  feedbackSettings: FeedbackSettings
}

export type TestV2 = Test
